import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  updateTenantAutoAcceptReceivedCfe,
  type TenantCurrentResponse,
} from "@/services/authService";
import { tenantKeys } from "@/lib/queryKeys";

export interface SettingsAutoAcceptReceivedCfeCardProps {
  tenant: TenantCurrentResponse;
}

/**
 * Settings card for auto-accept of received CFEs (DGI §XVIII, 20-día
 * window). On by default, and accepts at arrival — turning it off means the
 * tenant takes full responsibility for accepting/rejecting received
 * comprobantes before the deadline, with the day-19 cron sending a
 * "vence mañana" push reminder instead of accepting.
 */
export function SettingsAutoAcceptReceivedCfeCard({
  tenant,
}: SettingsAutoAcceptReceivedCfeCardProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [enabled, setEnabled] = useState<boolean>(
    tenant.autoAcceptReceivedCfe !== false,
  );
  useEffect(() => {
    setEnabled(tenant.autoAcceptReceivedCfe !== false);
  }, [tenant.autoAcceptReceivedCfe]);

  const mutation = useMutation({
    mutationFn: (next: boolean) => updateTenantAutoAcceptReceivedCfe(next),
    onMutate: (next) => {
      const previous = enabled;
      setEnabled(next);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx) setEnabled(ctx.previous);
      toast.error(
        t(
          "settings.autoAcceptReceivedCfe.saveError",
          "No se pudo guardar el ajuste.",
        ),
      );
    },
    onSuccess: async (_data, next) => {
      await qc.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success(
        next
          ? t(
              "settings.autoAcceptReceivedCfe.toastEnabled",
              "Aceptación automática activada",
            )
          : t(
              "settings.autoAcceptReceivedCfe.toastDisabled",
              "Aceptación automática desactivada",
            ),
      );
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-foreground">
          {t(
            "settings.autoAcceptReceivedCfe.title",
            "Aceptación automática de comprobantes recibidos",
          )}
        </CardTitle>
        <CardDescription>
          {t(
            "settings.autoAcceptReceivedCfe.description",
            "DGI exige que aceptes o rechaces cada CFE recibido dentro de los 20 días (§XVIII). Si está activado, Fiscalo los acepta automáticamente al recibirlos, así no se te acumulan pendientes y siempre quedás dentro del plazo.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3">
          <Checkbox
            id="autoAcceptReceivedCfe"
            checked={enabled}
            disabled={mutation.isPending}
            onCheckedChange={(value) => {
              const next = value === true;
              if (next === enabled || mutation.isPending) return;
              mutation.mutate(next);
            }}
          />
          <div className="flex-1 space-y-1">
            <Label
              htmlFor="autoAcceptReceivedCfe"
              className="cursor-pointer text-sm font-medium"
            >
              {t(
                "settings.autoAcceptReceivedCfe.toggleLabel",
                "Aceptar automáticamente al recibir",
              )}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                "settings.autoAcceptReceivedCfe.toggleHint",
                "Si lo desactivás, cada comprobante queda pendiente para que lo revises y Fiscalo te avisa antes del vencimiento en lugar de aceptar — vas a tener que actuar antes que se cumpla el plazo o el comprobante quedará sin respuesta y podés tener problemas con DGI. Desactivalo si necesitás poder rechazar comprobantes incorrectos.",
              )}
            </p>
          </div>
          {mutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
